if (!customElements.get('newsletter-popup')) {
  customElements.define(
    'newsletter-popup',
    class NewsletterPopup extends HTMLElement {
      connectedCallback() {
        this.storageKey = `palmare-newsletter-popup-dismissed-${this.id}`;
        this.frequencyDays = parseFloat(this.dataset.frequencyDays || '1');

        const closeButton = this.querySelector('.newsletter-popup__close');
        const overlay = this.querySelector('.newsletter-popup__overlay');
        if (closeButton) closeButton.addEventListener('click', this.dismiss.bind(this));
        if (overlay) overlay.addEventListener('click', this.dismiss.bind(this));
        this.onKeyUp = this.onKeyUp.bind(this);
        document.addEventListener('keyup', this.onKeyUp);

        // Inside the Theme Editor, Shopify re-renders this section's HTML on
        // every settings tweak, re-running connectedCallback each time. A
        // full-viewport overlay auto-opening on (near-)every render — on top
        // of overflow-hidden on <body> — was intercepting the clicks the
        // Customizer needs to select/edit sections, making the preview feel
        // frozen. Never auto-trigger in design mode; only allow showing it
        // on demand while this exact section is selected, so merchants can
        // still preview it.
        if (window.Shopify && window.Shopify.designMode) {
          this.onSectionSelect = this.onSectionSelect.bind(this);
          this.onSectionDeselect = this.onSectionDeselect.bind(this);
          document.addEventListener('shopify:section:select', this.onSectionSelect);
          document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
          this.watchForBodyLock();
          return;
        }

        // A successful subscription re-renders this section server-side with
        // the form still posted; keep the popup open to show the confirmation
        // instead of re-evaluating the delay/exit-intent/frequency rules.
        if (this.dataset.postedSuccessfully === 'true') {
          this.show();
          return;
        }

        if (this.shouldStaySuppressed()) return;

        const trigger = this.dataset.trigger;
        if (trigger === 'delay' || trigger === 'both') {
          // Capped at 500ms regardless of the configured setting: the popup
          // should feel instant, just giving the DOM a moment to paint
          // rather than interrupting the visitor mid-scroll after several
          // seconds.
          // 09/10/2026: en MÓVIL (tráfico frío de TikTok/Meta) el popup a los 0,5 s se apilaba sobre el banner de
          // cookies y tapaba la ficha: 1.366 visitas de TikTok en 14 días y 0 carritos. En móvil espera 30 s y
          // nunca se abre mientras el banner de cookies siga en pantalla. En escritorio no cambia.
          const isMobile = window.matchMedia('(max-width: 749px)').matches;
          const delay = isMobile ? 30000 : Math.min(parseFloat(this.dataset.delay || '0') * 1000, 500);
          this.delayTimeoutId = setTimeout(() => this.showWhenClear(), delay);
        }
        if (trigger === 'exit_intent' || trigger === 'both') {
          this.onExitIntent = this.onExitIntent.bind(this);
          document.addEventListener('mouseout', this.onExitIntent);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('keyup', this.onKeyUp);
        if (this.onExitIntent) document.removeEventListener('mouseout', this.onExitIntent);
        if (this.onSectionSelect) document.removeEventListener('shopify:section:select', this.onSectionSelect);
        if (this.onSectionDeselect) document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
        if (this.delayTimeoutId) clearTimeout(this.delayTimeoutId);
        if (this.bodyLockObserver) this.bodyLockObserver.disconnect();
      }

      // Failsafe for the Theme Editor: even with show() skipping the
      // body-scroll lock in design mode (see below), watch for it anyway —
      // via class or inline style — and strip it immediately. This guards
      // against any other script (ours or Shopify's own) leaving <body> or
      // <html> scroll-locked while a merchant is working in the Customizer.
      watchForBodyLock() {
        const stripLock = (el) => {
          if (!el) return;
          if (el.classList.contains('overflow-hidden')) el.classList.remove('overflow-hidden');
          if (el.style.overflow === 'hidden') el.style.removeProperty('overflow');
        };

        this.bodyLockObserver = new MutationObserver(() => {
          stripLock(document.body);
          stripLock(document.documentElement);
        });
        this.bodyLockObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
        this.bodyLockObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });
      }

      cookieBannerVisible() {
        const b = document.getElementById('shopify-pc__banner');
        return !!b && b.getBoundingClientRect().height > 0 && getComputedStyle(b).visibility !== 'hidden';
      }

      // Abre el popup solo cuando el banner de cookies ya no está; reintenta cada 3 s (máx. 20 veces).
      showWhenClear(tries = 0) {
        if (this.cookieBannerVisible() && tries < 20) {
          this.delayTimeoutId = setTimeout(() => this.showWhenClear(tries + 1), 3000);
          return;
        }
        this.show();
      }

      onSectionSelect(event) {
        if (event.detail?.sectionId === this.dataset.sectionId) this.show();
      }

      onSectionDeselect(event) {
        if (event.detail?.sectionId === this.dataset.sectionId) this.dismiss();
      }

      onExitIntent(event) {
        if (event.clientY <= 0) this.show();
      }

      onKeyUp(event) {
        if (event.code === 'Escape' && this.classList.contains('is-visible')) this.dismiss();
      }

      shouldStaySuppressed() {
        if (this.frequencyDays <= 0) return false;
        const dismissedAt = window.localStorage.getItem(this.storageKey);
        if (!dismissedAt) return false;
        const elapsedDays = (Date.now() - parseInt(dismissedAt, 10)) / 86400000;
        return elapsedDays < this.frequencyDays;
      }

      show() {
        if (this.classList.contains('is-visible')) return;
        this.classList.add('is-visible');
        // Never lock body scroll in the Theme Editor: this same method runs
        // when a merchant selects this section to preview it, and a locked
        // <body> there reads as a frozen Customizer.
        if (!(window.Shopify && window.Shopify.designMode)) {
          document.body.classList.add('overflow-hidden');
        }
        if (this.delayTimeoutId) clearTimeout(this.delayTimeoutId);
        if (this.onExitIntent) document.removeEventListener('mouseout', this.onExitIntent);
      }

      dismiss() {
        this.classList.remove('is-visible');
        document.body.classList.remove('overflow-hidden');
        try {
          window.localStorage.setItem(this.storageKey, Date.now().toString());
        } catch (error) {
          // Storage may be unavailable (private browsing); fail silently.
        }
      }
    }
  );
}
